import React from "react";
import { TableCell } from "@mui/material";
import { DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import MentionInputRender from "@common/components/mentionInput/MentionInputRender";
import { useUrlProvider } from "../useUrlProvider";
import { ControllerProcess } from "../processingActivity/processingActivityOverview/ProcessingActivityOverview.types";
import { DataControllerRecordModel, DataControllerRecordRecipient } from "./DataControllerRecord.types";
import RecordNameWithSubNames from "./RecordNameWithSubNames";
import FormattedDataCategories from "./FormattedDataCategories";

export interface DataControllerRecordCellsProps {
    row: DataControllerRecordModel;
    isExtended: boolean;
}

// The cells of one record row, in the order getDataControllerRecordHeaders lists the columns.
function DataControllerRecordCells(props: DataControllerRecordCellsProps) {
    const { row, isExtended } = props;
    const { translateString } = useTranslation();
    const { getProcessingActivityOverviewUrl } = useUrlProvider();

    const renderRecipients = (recipients: Array<DataControllerRecordRecipient>) => {
        return recipients.map((recipient, index) => (
            <RecordNameWithSubNames
                key={index}
                name={`${recipient.name} (${recipient.countryCode}) (${recipient.sharingRoles.map((role) => translateString(ControllerProcess[Number(role)])).join(", ")})`}
                subNames={recipient.dataCategories}
            />
        ));
    };

    const renderLegalBases = (legalBases: Array<string>) => {
        return legalBases.map((legalBasis, index) => (
            <React.Fragment key={index}>
                <div>{legalBasis}</div>
                <br />
            </React.Fragment>
        ));
    };

    return (
        <React.Fragment>
            <TableCell>
                <DotLegalLink bold linkColor="primary" to={getProcessingActivityOverviewUrl(row.id)}>
                    {row.name}
                </DotLegalLink>
            </TableCell>

            {isExtended && (
                <React.Fragment>
                    <TableCell>{row.businessAreas}</TableCell>
                    <TableCell>
                        <MentionInputRender value={row.description} />
                    </TableCell>
                </React.Fragment>
            )}

            <TableCell>{row.purposes}</TableCell>
            <TableCell>
                <FormattedDataCategories dataCategories={row.dataCategories} />
            </TableCell>

            {isExtended && (
                <React.Fragment>
                    <TableCell>{renderLegalBases(row.legalBases)}</TableCell>
                    <TableCell>{renderLegalBases(row.disclosureBases)}</TableCell>
                </React.Fragment>
            )}

            <TableCell>
                {row.dataSubjects.map((dataSubject, index) => (
                    <RecordNameWithSubNames
                        key={index}
                        name={dataSubject.name}
                        subNames={dataSubject.dataCategories.map(
                            (dataCategory) => `${dataCategory.name} ${dataCategory.dataTypes.length > 0 ? `(${dataCategory.dataTypes.join(", ")})` : ""}`
                        )}
                    />
                ))}
            </TableCell>
            <TableCell>
                {row.deletionPeriods.map((deletionPeriod, index) => (
                    <RecordNameWithSubNames key={index} name={deletionPeriod.name} subNames={deletionPeriod.dataCategories} />
                ))}
            </TableCell>
            <TableCell>{row.securityMeasures}</TableCell>

            {isExtended && <TableCell>{row.systems}</TableCell>}

            <TableCell>{renderRecipients(row.recipientsEuEea)}</TableCell>
            <TableCell>{renderRecipients(row.recipientsAdequate)}</TableCell>
            <TableCell>{renderRecipients(row.recipientsThirdCountry)}</TableCell>

            {isExtended && <TableCell>{renderLegalBases(row.transferBases)}</TableCell>}
        </React.Fragment>
    );
}

export default DataControllerRecordCells;
